import React, { FC, useCallback, useState } from "react";
import { useLocation } from 'react-router-dom';

import { generateFollowThroughPattern } from "@xapp/arachne-utils";
import {
    Handler,
    App as StentorApp,
    Entity as StentorEntity,
    Handler as StentorHandler,
    Intent as StentorIntent,
    APP_STATUS_INCOMPLETE
} from "stentor-models";

import {
    CreateOrgAndAppOnboardingMutationVariables,
    UpdateAppInput,
    useAnalyzeWebsiteMutation,
    useCreateOrgAndAppOnboardingMutation,
    UserProfile,
    useStartCrawlMutation,
    useUpdateApplicationMutation,
    useUpdateStatusMutation
} from "generated/graphql";

import { wrapUrqlMutation, convertHandler } from "utils/graphql";
import { useBoolState } from "utils/bool";

import { SimpleConfigureAndCreateStep, SimpleConfigureAndCreateStepProps } from "../components/SimpleConfigureAndCreateStep";
import { CreateProps } from "../models";

import { getProducts } from "utils/getProducts";
import { cleanObj } from "utils/Json";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

export interface SimpleConfigureAndCreateStepContainerProps extends Omit<SimpleConfigureAndCreateStepProps, "onCreate"> {
    /**
     * Profile of the user
     */
    profile: UserProfile;
    /**
     * Selected product
     */
    product: string;
    /**
     * The user is ready for the next step
     * @param app
     */
    onNextStep(props: CreateProps): void;
}

export const SimpleConfigureAndCreateStepContainer: FC<SimpleConfigureAndCreateStepContainerProps> = props => {

    const { onNextStep, product: productId, profile } = props;

    const queryParams = useQueryParams();

    const [creationStatusMessage, setCreationStatusMessage] = useState<string>();
    const [creating, stopCreating, startCreating] = useBoolState(false);
    const [error, setError] = useState<{ error: string, message: string }>();

    const products = getProducts();
    const product = products.find((product) => product.id === productId);

    /**
     * Define some mutations we will use during creation.
     *
     * We want to suppress the global alerts and errors and instead display them on the page itself
     */
    const createApp = wrapUrqlMutation(useCreateOrgAndAppOnboardingMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the app for you." });
            return { send: false }
        }
    });

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the contact information on the app for you." });
            return { send: false }
        }
    });

    const startCrawl = wrapUrqlMutation(useStartCrawlMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false }
        }
    });

    const analyzeSite = wrapUrqlMutation(useAnalyzeWebsiteMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false }
        }
    });

    const updateStatus = wrapUrqlMutation(useUpdateStatusMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the status for you." });
            return { send: false }
        }
    });

    /**
     * The main method fo app creation, it all of the mutations.
     *
     * We will want to refactor this to make it more potable and reusable.
     */
    const handleCreate = useCallback(async (createProps: CreateProps) => {
        startCreating();

        createProps = cleanObj(createProps);

        // Update the current fields in case of an error
        sessionStorage.setItem("onboarding-appProps", JSON.stringify(createProps));

        // load the template JSON
        const templateName = createProps.template?.name || "capture leads...";
        setCreationStatusMessage(`Preparing data for ${templateName}...`);
        const templateUrl = createProps?.template?.url || "https://templates.xapp.ai/contact-capture.json";
        const templateId = createProps?.template?.id || "contact-capture";

        type Template = { app: StentorApp, entities: StentorEntity[], intents: StentorIntent[], handlers: StentorHandler[] };

        const template: Template = await fetch(templateUrl)
            .then((response) => {
                return response.json();
            }).catch((e) => {
                setError({ message: `Could not load the template: ${templateId}`, error: e.message });
                stopCreating();
            });

        if (!template) {
            // bail
            return;
        }
        setCreationStatusMessage(`Template ${template.app.name} loaded...`)
        /**
         * No model for our templates so we clean off utterances & don't pass intents & entities
         */
        const cleanHandler = (handler: Handler): Handler => {
            // delete slots
            const cleaned = { ...handler };
            cleaned.slots = [];
            cleaned.utterancePatterns = [];

            return cleaned;
        }

        const handlers = template.handlers.map(cleanHandler).map(convertHandler);

        const variables: CreateOrgAndAppOnboardingMutationVariables = {
            app: {
                app: {
                    name: createProps.name,
                    businessDescription: createProps.businessDescription || undefined,
                    website: createProps.website || undefined,
                    templateType: templateId,
                    icon: createProps.logo
                    // TODO: Ability to add leadsContact & leadsPhoneContact & website
                },
                // they are empty so we don't create a model
                entities: [],
                intents: [],
                handlers
            },
            org: {
                name: createProps.name,
                logoUrl: createProps.logo
            }
        };

        setCreationStatusMessage("Creating app...");

        const result = await createApp(variables);

        const app = result?.data?.org?.add?.app;

        if (!app) {
            stopCreating();
            return;
        }

        const { appId, organizationId } = app;
        createProps.appId = appId;
        createProps.organizationId = organizationId;

        const updateAppInput: UpdateAppInput = {}

        const partner: string | undefined = queryParams.get("partner") || product?.metadata?.["partner"];

        if (partner) {
            updateAppInput.refernceId = {
                service: partner,
                // we don't know this yet
                id: "unknown"
            }
        }

        updateAppInput.beta = createProps.beta;

        await updateApplication({
            appId,
            app: updateAppInput
        });


        if (app.website) {
            // Start Crawl
            setCreationStatusMessage("Starting website ingestion...");
            await new Promise(r => setTimeout(r, 500));

            const pattern: string[] = [generateFollowThroughPattern(app.website)];

            await startCrawl({
                appId,
                url: app.website,
                pattern,
                channelId: ""
                //    channelId:  widget.id
            });

            const result = await analyzeSite({ url: createProps.website }).catch((e) => {
                console.error("Failed to analyze site", e);
            });

            if (result) {
                createProps.executionId = result?.data?.onboarding?.startRetrieveWebsiteDetails?.executionId;
            }
        }

        await updateStatus({
            appId,
            type: APP_STATUS_INCOMPLETE,
            notes: `Created ${new Date().toDateString()} by ${profile.email} during onboarding.`
        });

        // Done
        setCreationStatusMessage(`Preparing business address...`);
        // Delay for 1.5 s
        await new Promise(r => setTimeout(r, 200));

        stopCreating();

        // Move on!
        onNextStep(createProps);
    }, [analyzeSite, createApp, onNextStep, product?.metadata, profile.email, queryParams, startCrawl, startCreating, stopCreating, updateApplication, updateStatus]);

    return (
        <SimpleConfigureAndCreateStep
            {...props}
            creating={creating}
            creatingStatusMessage={creationStatusMessage}
            onCreate={handleCreate}
            error={error}
        />
    );
}